import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import { NativeSelect } from '@/components/ui/NativeSelect'
import { SearchField } from '@/components/ui/SearchField'
import { formatDay, formatMoney } from '@/lib/format'
import type { AdminFetch } from './AdminCustomers'
import { holdingLabels, stateTone } from './adminLicenseText'

type Filter = 'ATTENTION' | 'PENDING' | 'EXPIRING' | 'EXPIRED' | 'ACTIVE' | 'ALL'
type State = 'PENDING' | 'REJECTED' | 'NONE' | 'EXPIRED' | 'EXPIRING' | 'ACTIVE'
type LicenseRow = {
  origin: 'PORTAL' | 'SWING'; id: string; companyId?: string | null; businessName: string; modality: string; holdings?: string | null
  expiresOn?: string | null; amountCents?: number | null; period?: 'MONTH' | 'YEAR' | 'MIXED' | null; state: State; contact?: string | null
}
type LicensePage = { items: LicenseRow[]; page: number; hasMore: boolean }

const filterLabels: Record<Filter, string> = { ATTENTION: 'Requieren atención', PENDING: 'Por validar', EXPIRING: 'Por vencer', EXPIRED: 'Vencidas', ACTIVE: 'Vigentes', ALL: 'Todas' }
const originLabels: Record<LicenseRow['origin'], string> = { PORTAL: 'Portal', SWING: 'Swing' }
const modalityLabels: Record<string, string> = { MANAGED_CLOUD: 'Nube', SWING: 'Swing' }
const stateLabels: Record<State, string> = { PENDING: 'Por validar', REJECTED: 'Rechazada', NONE: 'Sin licencia', EXPIRED: 'Vencida', EXPIRING: 'Por vencer', ACTIVE: 'Vigente' }
const periodSuffix: Record<string, string> = { YEAR: '/año', MONTH: '/mes', MIXED: ' mixto' }
const emptyCopy: Record<Filter, string> = {
  ATTENTION: 'Nada por validar, vencido ni por vencer en los próximos 15 días.',
  PENDING: 'Ningún servidor de Swing espera validación.',
  EXPIRING: 'Ninguna licencia vence en los próximos 15 días.',
  EXPIRED: 'No hay licencias vencidas.',
  ACTIVE: 'No hay licencias vigentes.',
  ALL: 'Aún no hay negocios en el portal ni servidores de Swing identificados.',
}

/** Every licence the platform answers for, the portal's environments and the Swing servers, what needs the admin first. */
export function AdminLicenses({ adminFetch, describeError, onOpenSwing, refreshKey = 0 }: {
  adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string; onOpenSwing: (id: string) => void; refreshKey?: number
}) {
  const [filter, setFilter] = useState<Filter>('ATTENTION')
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<LicenseRow[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const lastLoad = useRef(0)

  const load = useCallback(async (chosen: Filter, text: string, nextPage = 0) => {
    const ticket = ++lastLoad.current
    setLoading(true); setError('')
    try {
      const result = await adminFetch<LicensePage>(`/admin/licenses?filter=${chosen}&q=${encodeURIComponent(text.trim())}&page=${nextPage}`)
      if (ticket !== lastLoad.current) return
      setRows((current) => nextPage === 0 ? result.items : [...current, ...result.items])
      setPage(result.page); setHasMore(result.hasMore)
    } catch (cause) {
      if (ticket === lastLoad.current) setError(describeError(cause, 'No pudimos leer las licencias.'))
    } finally {
      if (ticket === lastLoad.current) setLoading(false)
    }
  }, [adminFetch, describeError])

  // The filter, the search and a change made in the Swing panel all reread the first page.
  useEffect(() => { void load(filter, query) }, [filter, query, load, refreshKey])

  return <section className="admin-customers admin-licenses" aria-labelledby="admin-licenses-heading">
    <div className="admin-section-heading"><div><h2 id="admin-licenses-heading">Licencias</h2><p>Los negocios del portal y los servidores de BsPOS Swing en una sola lista: primero lo que espera validación, después lo vencido y lo que vence en 15 días.</p></div></div>
    <div className="admin-list-controls">
      <NativeSelect label="Mostrar" value={filter} onChange={(event) => setFilter(event.target.value as Filter)}>
        {(Object.keys(filterLabels) as Filter[]).map((value) => <option key={value} value={value}>{filterLabels[value]}</option>)}
      </NativeSelect>
      <SearchField value={query} label="Buscar licencias" placeholder="Negocio, contacto o correo" onCommit={(value) => setQuery(value)} />
    </div>
    {error && rows.length > 0 && <Notice>{error}</Notice>}
    {loading && rows.length === 0 ? <LoadingState label="Leyendo las licencias…" />
      : error && rows.length === 0 ? <ErrorState title={error} retry={() => void load(filter, query)} busy={loading} />
      : rows.length === 0 ? <EmptyState title={query.trim() ? 'Ninguna licencia coincide' : 'Nada en esta lista'} description={query.trim() ? 'Prueba con otra parte del nombre del negocio, del contacto o del correo.' : emptyCopy[filter]} />
      : <div className="price-table-wrap admin-licenses-table"><table>
        <thead><tr><th><span className="sr-only">Acciones</span></th><th>Negocio</th><th>Origen</th><th>Modalidad</th><th>Licencias</th><th>Vence</th><th>Estado</th><th className="admin-amount">Importe</th></tr></thead>
        <tbody>{rows.map((row) => {
          const held = holdingLabels(row.holdings)
          return <tr key={`${row.origin}-${row.id}`}>
            <td>{row.origin === 'SWING' ? <Button emphasis="outline" intent="neutral" onClick={() => onOpenSwing(row.id)} aria-label={`Ver ${row.businessName}`}>Ver</Button> : null}</td>
            <td><strong>{row.businessName}</strong>{row.contact && <span className="admin-customers-sub">{row.contact}</span>}</td>
            <td>{originLabels[row.origin] ?? row.origin}</td>
            <td>{modalityLabels[row.modality] ?? row.modality}</td>
            <td>{held.length === 0 ? <span className="field-hint">Nada vigente</span> : <ul className="module-list">{held.map((label) => <li key={label}>{label}</li>)}</ul>}</td>
            <td>{formatDay(row.expiresOn)}</td>
            <td><span className="status-chip" data-tone={stateTone(row.state)}>{stateLabels[row.state] ?? row.state}</span></td>
            <td className="admin-amount">{row.amountCents == null ? '—' : `${formatMoney(row.amountCents)}${row.period ? periodSuffix[row.period] ?? '' : ''}`}</td>
          </tr>
        })}</tbody>
      </table></div>}
    {!error && hasMore && <div className="admin-customers-more"><Button emphasis="outline" intent="neutral" busy={loading} onClick={() => void load(filter, query, page + 1)}>Más licencias</Button></div>}
  </section>
}
